const CART_DRAWER_V2_UPSELL_SIZE_SUFFIXES = Object.freeze({
  Single: 'single',
  'Small Double': 'small-double',
  Double: 'double',
  King: 'king',
  'Super King': 'super-king',
});

// Mattress -> starter bundle mapping. Ported from Nectar, where this was hardcoded
// against that catalogue. Here it is supplied from the cart-drawer section settings
// (Smart Upsell > Mattress bundle mapping) so the mapping can be maintained without
// a theme deploy. This constant is only the fallback when no mapping is configured.
const CART_DRAWER_V2_UPSELL_MATTRESS_CONFIGS = Object.freeze([]);

if (!customElements.get('cart-drawer-v2-upsell')) {
  class CartDrawerV2Upsell extends HTMLElement {
    constructor() {
      super();

      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);

      this.productCache = new Map();
      this.bundleConfigCache = new Map();
      this.refreshPromise = Promise.resolve();
      this.dragState = null;
      this.pendingOfferHandle = null;
    }

    connectedCallback() {
      if (this.connectedOnce) {
        this.bindEvents();
        this.queueRefresh();
        return;
      }

      this.connectedOnce = true;
      this.config = this.parseConfig();

      if (!this.config.enabled) {
        this.hidden = true;
        return;
      }

      this.bindEvents();
      this.queueRefresh();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    parseConfig() {
      const rawConfig = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.textContent;

      if (!rawConfig) {
        return { enabled: false };
      }

      try {
        return JSON.parse(rawConfig);
      } catch (error) {
        console.error('[CartDrawerV2Upsell] Failed to parse config', error);
        return { enabled: false };
      }
    }

    bindEvents() {
      if (this.bound) {
        return;
      }

      this.bound = true;
      document.addEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.addEventListener('cart:updated', this.handleCartUpdate);
      this.addEventListener('click', this.handleClick);
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointercancel', this.handlePointerUp);
    }

    unbindEvents() {
      if (!this.bound) {
        return;
      }

      this.bound = false;
      document.removeEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.removeEventListener('cart:updated', this.handleCartUpdate);
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointercancel', this.handlePointerUp);

      if (this.carousel) {
        this.carousel.removeEventListener('pointerdown', this.handlePointerDown);
      }
    }

    handleCartUpdate() {
      this.queueRefresh();
    }

    queueRefresh() {
      this.refreshPromise = this.refreshPromise
        .catch(() => undefined)
        .then(() => this.refresh());

      return this.refreshPromise;
    }

    async refresh() {
      const cart = await this.getCart();
      const state = this.resolveState(cart);

      if (!state) {
        this.hidden = true;
        this.innerHTML = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';
        this.remountCartDrawerInteractions();
        return;
      }

      const offers = [];

      for (const offer of state.offerRequests) {
          const resolvedOffer = await this.resolveOffer(offer, state);

        if (resolvedOffer) {
          offers.push(resolvedOffer);
        }
      }

      if (!offers.length) {
        this.hidden = true;
        this.innerHTML = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';
        this.remountCartDrawerInteractions();
        return;
      }

      this.currentState = state;
      this.currentOffers = offers;
      this.hidden = false;
      this.render();
      this.remountCartDrawerInteractions();
    }

    remountCartDrawerInteractions() {
      if (!window.__Theme?.remountComponent) {
        return;
      }

      const cartDrawer = this.closest('#CartDrawerV2');

      if (!cartDrawer) {
        return;
      }

      const remountTargets = [
        {
          selector: '.cart-drawer-v2__totals__total__toggle[data-class-change]',
          types: ['class-change'],
        },
      ];

      remountTargets.forEach(({ selector, types, scope }) => {
        const root = scope === 'document' ? document : cartDrawer;

        root.querySelectorAll(selector).forEach((element) => {
          types.forEach((type) => {
            // Only remount components the element actually declares. Theme's
            // mountComponent does elem.getAttribute(`data-${type}`).trim(), which
            // throws on a missing attribute.
            if (!element.hasAttribute(`data-${type}`)) {
              return;
            }

            window.__Theme.remountComponent(element, type);
          });
        });
      });
    }

    async getCart() {
      const cartState = window.liquidAjaxCart?.getState?.()?.cart;

      if (cartState) {
        return cartState;
      }

      if (window.liquidAjaxCart?.cart) {
        return window.liquidAjaxCart.cart;
      }

      const response = await fetch('/cart.js');

      if (!response.ok) {
        return null;
      }

      return response.json();
    }

    resolveState(cart) {
      const cartItems = cart?.items || [];

      if (!cartItems.length || this.hasBundle(cartItems)) {
        return null;
      }

      const mattressItem = this.getMattressItem(cartItems);

      if (!mattressItem) {
        return null;
      }

      const size = this.extractSize(mattressItem.variant_title || mattressItem.title);

      if (!size) {
        return null;
      }

      const isKids = this.isKidsMattress(mattressItem);
      const protectorItem = this.getMatchingProtector(cartItems, size, isKids);

      if (isKids) {
        if (protectorItem) {
          return null;
        }

        return {
          step: 'mattress-only',
          size,
          isKids,
          mattressItem,
          progressValue: this.config.progress?.mattressOnly || 25,
          offerRequests: [
            {
              kind: 'protector',
              handleCandidates: [this.config.kidsProtectorHandle],
            },
          ],
        };
      }

      if (protectorItem) {
        return {
          step: 'with-protector',
          size,
          isKids: false,
          mattressItem,
          protectorItem,
          upgradeCredit: this.getUpgradeCredit(mattressItem, protectorItem),
          progressValue: this.config.progress?.withProtector || 75,
          offerRequests: [
            {
              kind: 'bundle',
              bundleType: 'starter',
              handleCandidates: this.getBundleCandidates('starter', size, mattressItem),
            },
            {
              kind: 'bundle',
              bundleType: 'ottoman',
              handleCandidates: this.getBundleCandidates('ottoman', size, mattressItem),
            },
          ],
        };
      }

      return {
        step: 'mattress-only',
        size,
        isKids: false,
        mattressItem,
        upgradeCredit: this.getUpgradeCredit(mattressItem, null),
        progressValue: this.config.progress?.mattressOnly || 25,
        offerRequests: [
          {
            kind: 'protector',
            handleCandidates: [this.config.protectorHandle],
          },
          {
            kind: 'bundle',
            bundleType: 'starter',
            handleCandidates: this.getBundleCandidates('starter', size, mattressItem),
          },
        ],
      };
    }

    hasBundle(cartItems) {
      return cartItems.some((item) => {
        // /cart.js line items carry product_type but not tags, so the tag check
        // only fires for cart shapes that include the product object.
        if (item.product?.tags?.includes('Shopify Bundle')) {
          return true;
        }

        if (String(item.product_type || '').toLowerCase().includes('bundle')) {
          return true;
        }

        if (item.handle?.includes('bundle')) {
          return true;
        }

        return Object.keys(item.properties || {}).some((key) => key.toLowerCase().includes('_bundleitem'));
      });
    }

    getMattressItem(cartItems) {
      // Handles named in the bundle mapping, so a mapped mattress is recognised
      // without depending on product_type being set. Matched on exact handle
      // rather than via getMattressConfig, whose searchTerms run against titles
      // and would let 'Hybrid Mattress Protector' pass as the mattress.
      const mappedHandles = this.getMattressConfigs()
        .map((config) => config.handle)
        .filter(Boolean);

      return cartItems.find((item) => {
        if (item.handle && mappedHandles.includes(item.handle)) {
          return true;
        }

        return item.product_type === 'Mattress' || [
          'classic-memory-foam-mattress',
          'classic-hybrid-mattress',
          'premier-memory-foam-mattress',
          'premier-hybrid-mattress',
          'kids-mattress',
        ].some((handle) => item.handle?.includes(handle));
      });
    }

    getMatchingProtector(cartItems, size, isKids) {
      return cartItems.find((item) => {
        const isProtector = isKids
          ? item.handle === this.config.kidsProtectorHandle
          : item.handle === this.config.protectorHandle || item.title?.toLowerCase().includes('protector');

        if (!isProtector) {
          return false;
        }

        if (isKids) {
          return true;
        }

        const protectorSize = this.extractSize(item.variant_title || item.title);

        return !protectorSize || protectorSize === size;
      }) || null;
    }

    isKidsMattress(item) {
      return item.handle === 'kids-mattress' || item.title?.toLowerCase().includes('kids mattress');
    }

    // Resolve a size name from a product or variant title. Matching is
    // longest-token-first so 'Small Double' wins over 'Double', and each size
    // can carry extra aliases (Smart Upsell > Size name aliases) for bundles
    // whose combined Size option is labelled '4ft6' or '135 x 190cm' rather
    // than the canonical size name.
    extractSize(title) {
      if (!title) {
        return null;
      }

      const haystack = String(title).toLowerCase();

      return this.getSizeMatchers().find(({ token }) => haystack.includes(token))?.size || null;
    }

    // Flat [{ size, token }] list sorted by token length descending. Flat rather
    // than grouped by size so a long alias on one size can never let a short
    // token on another size win: 'small double' must be tested before 'double'
    // no matter which size each belongs to.
    getSizeMatchers() {
      if (this.sizeMatchers) {
        return this.sizeMatchers;
      }

      const aliases = this.config.sizeAliases || {};
      const seen = new Set();

      this.sizeMatchers = Object.keys(this.getSizeSuffixes())
        .flatMap((size) => {
          const configured = Array.isArray(aliases[size]) ? aliases[size] : [];

          return [size, ...configured].map((token) => ({
            size,
            token: String(token).toLowerCase().trim(),
          }));
        })
        .filter(({ token }) => {
          if (!token || seen.has(token)) {
            return false;
          }

          seen.add(token);
          return true;
        })
        .sort((a, b) => b.token.length - a.token.length);

      return this.sizeMatchers;
    }

    getSizeSuffixes() {
      const configured = this.config.sizeSuffixes;

      if (configured && typeof configured === 'object' && Object.keys(configured).length) {
        return configured;
      }

      return CART_DRAWER_V2_UPSELL_SIZE_SUFFIXES;
    }

    getMattressConfigs() {
      const configured = this.config.mattressConfigs;

      if (Array.isArray(configured) && configured.length) {
        return configured;
      }

      return CART_DRAWER_V2_UPSELL_MATTRESS_CONFIGS;
    }

    async resolveOffer(offerRequest, state) {
      const resolved = await this.resolveProductAndVariant(offerRequest, state);

      if (!resolved) {
        return null;
      }

      const { product, variant } = resolved;

      let bundleItems = null;
      let totalValue = null;

      if (offerRequest.kind === 'bundle') {
        bundleItems = await this.fetchBundleItems(product.handle, state.mattressItem);
        totalValue = this.config.bundleTotalValues?.[product.handle]?.[variant.id] || null;
      } else if (offerRequest.kind === 'protector') {
        totalValue = this.config.protectorTotalValues?.[variant.id] || null;
      }

      return {
        ...offerRequest,
        product,
        variant,
        bundleItems,
        totalValue,
      };
    }

    // Resolve the product and its variant together instead of taking the first
    // handle that exists and then hoping it carries the cart's size. A candidate
    // that cannot serve the size is skipped so the next one gets a chance, which
    // is what lets both bundle models share one candidate list: a bundle product
    // per size, and a single bundle whose sizes are variants of a combined
    // option.
    async resolveProductAndVariant(offerRequest, state) {
      const handleCandidates = offerRequest.handleCandidates || [];
      const resolvedProducts = [];

      // Pass 1 - strict: the candidate must be able to serve the cart's size.
      for (const handle of handleCandidates) {
        const product = await this.fetchProduct(handle);

        if (!product) {
          continue;
        }

        resolvedProducts.push(product);

        const variant = this.selectSizedVariant(product, state.size);

        if (variant) {
          return { product, variant };
        }
      }

      // Pass 2 - relaxed: a protector can legitimately be sold in a single size,
      // so fall back to any available variant. Bundles never relax - offering a
      // bundle in the wrong size is worse than offering none.
      if (offerRequest.kind === 'bundle') {
        return null;
      }

      for (const product of resolvedProducts) {
        const variant = this.findVariant(product, state.size);

        if (variant) {
          return { product, variant };
        }
      }

      return null;
    }

    // Pick the variant that serves `size`, deriving which bundle model this
    // product follows from the product itself rather than from configuration:
    //
    //   no size in any variant title - the size is encoded in the handle (one
    //     bundle product per size), so any available variant is the right one.
    //   size in the variant titles - combined options ('Combine options with
    //     matching names'), so the variant must match the cart's size exactly
    //     or this candidate cannot serve the cart at all.
    selectSizedVariant(product, size) {
      const available = (product?.variants || []).filter((variant) => variant.available);

      if (!available.length) {
        return null;
      }

      const expressesSize = product.variants.some((variant) => this.extractSize(variant.title));

      if (!expressesSize) {
        return available[0];
      }

      if (!size) {
        return null;
      }

      return available.find((variant) => this.extractSize(variant.title) === size) || null;
    }

    // Ordered handle candidates for a bundle offer, covering both bundle models
    // at once:
    //
    //   1. the size-agnostic handle(s) - a Shopify Bundles product whose Size is
    //      a combined option, one variant per size;
    //   2. the legacy per-size handle built from prefix + size suffix;
    //   3. any explicit fallbacks from the Bundle handle fallbacks setting.
    //
    // A mattress part-way through migration can carry both: the new bundle wins,
    // and the legacy per-size bundle still covers any size the new one cannot
    // build yet. Ordering is controlled purely by which fields are filled in -
    // there is no per-bundle mode flag.
    getBundleCandidates(kind, size, mattressItem) {
      const fallbackGroup = this.config.bundleHandleCandidates?.[kind] || {};
      const fallbacks = fallbackGroup[size] || fallbackGroup.any || [];
      const mattressConfig = this.getMattressConfig(mattressItem);
      const candidates = [];

      if (mattressConfig) {
        // `bundles[kind]` is the current shape. Without it, the flat legacy shape
        // is read for starter bundles only - and note that `handle` there is the
        // *mattress* handle used for matching, so it is never read as a bundle
        // handle.
        const bundleConfig = mattressConfig.bundles?.[kind] || (kind === 'starter'
          ? {
            handle: mattressConfig.bundleHandle,
            handles: mattressConfig.bundleHandles,
            prefix: mattressConfig.prefix,
            sizeSuffixes: mattressConfig.sizeSuffixes,
          }
          : {});

        candidates.push(...this.toHandleList(bundleConfig.handle));
        candidates.push(...this.toHandleList(bundleConfig.handles));

        const sizeSuffix = bundleConfig.sizeSuffixes?.[size]
          || mattressConfig.sizeSuffixes?.[size]
          || this.getSizeSuffixes()[size];

        if (bundleConfig.prefix && sizeSuffix) {
          candidates.push(`${bundleConfig.prefix}-${sizeSuffix}`);
        }
      }

      candidates.push(...this.toHandleList(fallbacks));

      return Array.from(new Set(candidates));
    }

    toHandleList(value) {
      if (!value) {
        return [];
      }

      const values = Array.isArray(value) ? value : [value];

      return values.map((handle) => String(handle).trim()).filter(Boolean);
    }

    getMattressConfig(mattressItem) {
      if (!mattressItem) {
        return null;
      }

      const title = String(mattressItem.title || '').toLowerCase();

      return [...this.getMattressConfigs()]
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .find((config) => {
          const searchTerms = Array.isArray(config.searchTerms) ? config.searchTerms : [];

          return config.handle === mattressItem.handle || searchTerms.some((term) => title.includes(String(term).toLowerCase()));
        }) || null;
    }

    getMattressType(mattressItem) {
      return this.getMattressConfig(mattressItem)?.displayName || 'Mattress';
    }

    async fetchProduct(handle) {
      if (!handle) {
        return null;
      }

      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      try {
        const response = await fetch(`/products/${handle}.js`);

        if (!response.ok) {
          this.productCache.set(handle, null);
          return null;
        }

        const product = await response.json();
        this.productCache.set(handle, product);
        return product;
      } catch (error) {
        this.productCache.set(handle, null);
        return null;
      }
    }

    findVariant(product, preferredSize) {
      if (!product?.variants?.length) {
        return null;
      }

      if (preferredSize) {
        const matchingVariant = product.variants.find((variant) => {
          return variant.available && this.extractSize(variant.title) === preferredSize;
        });

        if (matchingVariant) {
          return matchingVariant;
        }
      }

      return product.variants.find((variant) => variant.available) || null;
    }

    async fetchBundleItems(handle, mattressItem) {
      if (!handle) {
        return null;
      }

      const cacheKey = `${handle}::${mattressItem?.handle || 'default'}`;

      if (this.bundleConfigCache.has(cacheKey)) {
        return this.bundleConfigCache.get(cacheKey);
      }

      // Fallback if the bundle's own component list is unavailable or does not
      // cover the mattress: the mattress from the cart.
      const mattressFallback = mattressItem ? [this.getMattressType(mattressItem)] : [];
      let result = mattressFallback.length ? mattressFallback : null;

      try {
        const response = await fetch(`/products/${handle}?view=json`);

        if (response.ok) {
          const bundleConfig = await response.json();
          const items = Array.isArray(bundleConfig?.bundleItems)
            ? bundleConfig.bundleItems.filter(Boolean).map((item) => this.titleCase(item))
            : [];

          // A Shopify Bundles product lists its mattress as a real component, so
          // prepending the cart's mattress would duplicate it. Legacy bundle_group
          // products often do not, so there the cart's mattress is kept. The JSON
          // view reports which case this is.
          const bundleItems = bundleConfig?.bundleIncludesMattress
            ? items
            : [...mattressFallback, ...items];

          result = bundleItems.length ? bundleItems : null;
        }
      } catch (error) {
        // Keep the mattress-only fallback.
      }

      this.bundleConfigCache.set(cacheKey, result);
      return result;
    }

    // Line-item properties for an offer, mirroring what the bundle PDP posts
    // (snippets/product-2026-bundle-atc.liquid) so a bundle added from the cart
    // drawer renders the same as one added from its product page. Derived from
    // the resolved variant, so it is correct for both bundle models: a per-size
    // bundle product has no size option and contributes no Size property, while
    // a combined-options bundle contributes Size and Colour.
    buildLineItemProperties(offer) {
      const properties = {};

      if (offer.kind !== 'bundle') {
        return properties;
      }

      if (Array.isArray(offer.bundleItems)) {
        offer.bundleItems.forEach((item, index) => {
          properties[`_bundleItem${index + 1}`] = item;
        });
      }

      const { size, colour } = this.getVariantOptionValues(offer.product, offer.variant);

      if (size) {
        properties.Size = size;
      }

      if (colour) {
        properties.Colour = colour;
      }

      // The PDP uses the variant's with_headboard_images metafield, which the
      // .js product endpoint does not expose; the variant image is the closest
      // equivalent available here.
      const cartImage = this.resolveOfferImage(offer);

      if (cartImage) {
        properties._cartImage = cartImage;
      }

      return properties;
    }

    // A bundle product may not have images of its own yet. Fall back through the
    // variant image, the product's own image list, and finally the mattress in the
    // cart that the bundle is built around, so the card never renders an <img>
    // with an empty src - which resolves to the page URL and refetches the
    // document.
    resolveOfferImage(offer) {
      const candidates = [
        offer.variant?.featured_image?.src,
        offer.product?.featured_image,
        offer.product?.images?.[0],
        this.currentState?.mattressItem?.featured_image?.url,
        this.currentState?.mattressItem?.image,
      ];

      return candidates.find((image) => typeof image === 'string' && image.trim()) || null;
    }

    // Split a variant's option values into size and colour by option name, the
    // same way the bundle PDP does. /products/{handle}.js returns product.options
    // as option objects on current API versions and as plain strings on older
    // ones, so both shapes are handled.
    getVariantOptionValues(product, variant) {
      const names = (product?.options || []).map((option) => String(option?.name ?? option ?? ''));
      const values = Array.isArray(variant?.options)
        ? variant.options
        : [variant?.option1, variant?.option2, variant?.option3];

      let size = '';
      let colour = '';

      names.forEach((name, index) => {
        const value = values[index];
        const lowerName = name.toLowerCase();

        if (!value || lowerName === 'title' || value === 'Default Title') {
          return;
        }

        if (lowerName.includes('colour') || lowerName.includes('color')) {
          colour = colour || value;
        } else if (!size) {
          size = value;
        }
      });

      return { size, colour };
    }

    titleCase(value) {
      return String(value)
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    }

    render() {
      const description = this.buildDescription();
      const configScript = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';

      this.innerHTML = `${configScript}
        <div class="cart-drawer-v2-upsell">
          <div class="cart-drawer-v2-upsell__surface">
            <div class="cart-drawer-v2-upsell__header">
              <h3 class="cart-drawer-v2-upsell__heading">${this.config.title}</h3>
              <div class="cart-drawer-v2-upsell__progress" aria-hidden="true">
                <span class="cart-drawer-v2-upsell__progress-bar" style="width: ${this.currentState.progressValue}%"></span>
              </div>
            </div>
            <p class="cart-drawer-v2-upsell__description">${description}</p>
            <div class="cart-drawer-v2-upsell__carousel" data-carousel>
              <div class="cart-drawer-v2-upsell__track" data-carousel-track>
                ${this.currentOffers.map((offer, index) => this.renderCard(offer, index)).join('')}
              </div>
            </div>
          </div>
        </div>
      `;

      this.carousel = this.querySelector('[data-carousel]');

      if (this.carousel) {
        this.carousel.removeEventListener('pointerdown', this.handlePointerDown);
        this.carousel.addEventListener('pointerdown', this.handlePointerDown);
      }
    }

    renderCard(offer, index) {
      const image = this.resolveOfferImage(offer);
      const cleanTitle = this.escapeHtml(this.cleanTitle(offer.product.title));
      const displayPrice = this.escapeHtml(`+${this.formatMoney(this.getOfferUpgradePrice(offer))}`);
      const detailsSize = this.escapeHtml(offer.variant.title || this.currentState.size || 'Default Title');
      const priceData = this.escapeHtml(String(offer.variant.price));
      const comparePriceData = this.escapeHtml(String(offer.variant.compare_at_price || ''));
      const url = this.escapeHtml(`/products/${offer.product.handle}`);
      // total_value metafields are held in pounds; the details modal wants pence.
      const totalValueData = offer.totalValue
        ? this.escapeHtml(String(Math.round(Number(offer.totalValue) * 100)))
        : '';
      const isLoading = this.pendingOfferHandle === offer.product.handle;

      return `
        <article class="cart-drawer-v2-upsell__card${isLoading ? ' is-loading' : ''}" data-offer-index="${index}" data-offer-handle="${this.escapeHtml(offer.product.handle)}">
          <div class="cart-drawer-v2-upsell__media">
            ${image ? `<img class="cart-drawer-v2-upsell__image" src="${this.escapeHtml(image)}" alt="${cleanTitle}" loading="lazy" width="160" height="160">` : ''}
          </div>
          <div class="cart-drawer-v2-upsell__content">
            <h4 class="cart-drawer-v2-upsell__title">${cleanTitle.toLowerCase()}</h4>
            <div class="cart-drawer-v2-upsell__meta">
              <modal-opener class="product-popup-modal__opener no-js-hidden" data-modal="#PopupModal-product-description">
                <button
                  class="cart-drawer-v2-upsell__details"
                  type="button"
                  data-size="${detailsSize}"
                  data-price="${priceData}"
                  data-compare_at_price="${comparePriceData}"
                  data-total-value="${totalValueData}"
                  data-url="${url}"
                  aria-haspopup="dialog"
                >
                  <span>Details</span>
                  <span class="cart-drawer-v2-upsell__details-icon" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 3C6.27614 3 6.5 3.22386 6.5 3.5V8.5C6.5 8.77614 6.27614 9 6 9C5.72386 9 5.5 8.77614 5.5 8.5V3.5C5.5 3.22386 5.72386 3 6 3Z" fill="currentColor"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M3 6C3 5.72386 3.22386 5.5 3.5 5.5H8.5C8.77614 5.5 9 5.72386 9 6C9 6.27614 8.77614 6.5 8.5 6.5H3.5C3.22386 6.5 3 6.27614 3 6Z" fill="currentColor"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1ZM0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6Z" fill="currentColor"/>
                    </svg>
                  </span>
                </button>
              </modal-opener>
              <p class="cart-drawer-v2-upsell__price">${displayPrice}</p>
              <div class="cart-drawer-v2-upsell__actions">
                <button class="cart-drawer-v2-upsell__add" type="button" data-upsell-add="${index}" ${isLoading ? 'disabled' : ''}>
                  <span
                    class="svg-wrapper cart-drawer-v2-upsell__add-icon"
                    aria-hidden="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 15.8333C9.76389 15.8333 9.56597 15.7535 9.40625 15.5938C9.24652 15.434 9.16666 15.2361 9.16666 15V10.8333H5C4.76389 10.8333 4.56597 10.7535 4.40625 10.5938C4.24653 10.434 4.16666 10.2361 4.16666 10C4.16666 9.76389 4.24653 9.56598 4.40625 9.40625C4.56597 9.24653 4.76389 9.16667 5 9.16667H9.16666V5.00001C9.16666 4.76389 9.24652 4.56598 9.40625 4.40626C9.56597 4.24653 9.76389 4.16667 10 4.16667C10.2361 4.16667 10.434 4.24653 10.5937 4.40626C10.7535 4.56598 10.8333 4.76389 10.8333 5.00001V9.16667H15C15.2361 9.16667 15.434 9.24653 15.5937 9.40625C15.7535 9.56598 15.8333 9.76389 15.8333 10C15.8333 10.2361 15.7535 10.434 15.5937 10.5938C15.434 10.7535 15.2361 10.8333 15 10.8333H10.8333V15C10.8333 15.2361 10.7535 15.434 10.5937 15.5938C10.434 15.7535 10.2361 15.8333 10 15.8333Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <span>Add</span>
                </button>
              </div>
            </div>
          </div>
        </article>
      `;
    }

    buildDescription() {
      if (this.currentState.step === 'mattress-only') {
        const protectorOffer = this.currentOffers.find((offer) => offer.kind === 'protector');
        const lowestPrice = protectorOffer ? this.formatMoney(protectorOffer.variant.price) : this.formatMoney(this.currentOffers[0].variant.price);
        const savingPercent = this.getMaxSavingsPercent();

        const protectorUpgradeText = this.currentState.isKids
          ? this.config.kidsProtectorUpgradeText
          : this.config.protectorUpgradeText;

        if (savingPercent > 0) {
          return protectorUpgradeText
            .replace('[PROTECTOR_COST]', this.escapeHtml(lowestPrice))
            .replace('[BUNDLE_PERCENTAGE]', savingPercent);
        }

        return protectorUpgradeText
          .replace('[PROTECTOR_COST]', this.escapeHtml(lowestPrice))
          .replace('[BUNDLE_PERCENTAGE]', '0');
      }

      const savingPercent = this.getMaxSavingsPercent();

      if (savingPercent > 0) {
        return this.config.bundleUpgradeText
          .replace('[BUNDLE_PERCENTAGE]', savingPercent);
      }

      return this.config.bundleUpgradeText
        .replace('[BUNDLE_PERCENTAGE]', '0');
    }

    // Adding a bundle swaps out what it replaces - removeBundleUpgradeItems
    // decrements the mattress and any matching protector - so a bundle card's
    // '+ X' is the cost of the upgrade, not the bundle's own price. Everything
    // else is a plain addition, where the variant price is already the extra
    // cost.
    //
    // The credit is the discounted cart price while the bundle side is the
    // catalogue price, since a bundle not yet in the cart has no discount
    // allocation to read. Where the same discount would also apply to the
    // bundle, the quoted upgrade is therefore the pessimistic figure.
    getOfferUpgradePrice(offer) {
      const price = Number(offer?.variant?.price || 0);

      if (offer?.kind !== 'bundle') {
        return price;
      }

      return Math.max(price - Number(this.currentState?.upgradeCredit || 0), 0);
    }

    getUpgradeCredit(mattressItem, protectorItem) {
      return [mattressItem, protectorItem].reduce((total, item) => total + this.getLineItemUnitPrice(item), 0);
    }

    // The unit price the customer is actually paying, so a line discount (a code
    // or an automatic discount allocated to the line) is credited back against
    // the upgrade: a 599 mattress discounted to 574 leaves 574 to swap out, not
    // 599. final_price carries line-level allocations; the fallbacks cover cart
    // shapes that omit it, and final_line_price / quantity covers the rest.
    getLineItemUnitPrice(item) {
      if (!item) {
        return 0;
      }

      const quantity = Math.max(Number(item.quantity || 1), 1);
      const candidates = [
        item.final_price,
        item.discounted_price,
        Number(item.final_line_price || 0) / quantity,
        item.price,
        item.original_price,
      ];

      const price = candidates.find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

      return Number(price || 0);
    }

    getOfferSavingsBaseline(offer) {
      if (!offer || offer.kind !== 'bundle') {
        return 0;
      }

      const compareAtPrice = Number(offer.variant?.compare_at_price || 0);

      if (compareAtPrice > 0) {
        return compareAtPrice;
      }

      const totalValue = Number(offer.totalValue || 0);

      if (totalValue > 0) {
        return totalValue * 100;
      }

      return 0;
    }

    getMaxSavingsPercent() {
      if (this.config.overrideBundleDiscountPercentage > 0) {
        return parseInt(this.config.overrideBundleDiscountPercentage, 10);
      }

      return this.currentOffers.reduce((maxValue, offer) => {
        const price = Number(offer.variant.price || 0);
        const savingsBaseline = this.getOfferSavingsBaseline(offer);

        if (savingsBaseline <= price || savingsBaseline <= 0) {
          return maxValue;
        }

        const savingsPercent = Math.round(((savingsBaseline - price) / savingsBaseline) * 100);

        if (!Number.isFinite(savingsPercent) || savingsPercent <= 0) {
          return maxValue;
        }

        return Math.max(maxValue, savingsPercent);
      }, 0);
    }

    cleanTitle(title) {
      return String(title)
        .replace(/^the dreamcloud\s+/i, '')
        .replace(/^dreamcloud\s+/i, '');
    }

    formatMoney(cents) {
      const moneyFormat = window.__Theme?._ctx?.getState?.()?.moneyFormat || window.Shopify?.money_format || '£{{amount}}';
      const value = (Number(cents || 0) / 100).toFixed(2);
      const [whole, fraction] = value.split('.');
      const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amount = fraction === '00' ? formattedWhole : `${formattedWhole}.${fraction}`;

      return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value ?? '');
      return div.innerHTML;
    }

    async handleClick(event) {
      const addButton = event.target.closest('[data-upsell-add]');

      if (!addButton) {
        return;
      }

      event.preventDefault();

      if (this.pendingOfferHandle) {
        return;
      }

      const offerIndex = Number(addButton.dataset.upsellAdd);
      const offer = this.currentOffers?.[offerIndex];

      if (!offer) {
        return;
      }

      this.pendingOfferHandle = offer.product.handle;
      this.render();

      try {
        const items = [
          {
            id: offer.variant.id,
            quantity: 1,
          },
        ];

        const properties = this.buildLineItemProperties(offer);

        if (Object.keys(properties).length) {
          items[0].properties = properties;
        }

        if (window.liquidAjaxCart?.add) {
          await window.liquidAjaxCart.add(
            { items },
            {
              info: {
                cartDrawerV2Upsell: true,
                offerHandle: offer.product.handle,
                offerKind: offer.kind,
              },
            }
          );
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(items[0]),
          });

          await window.liquidAjaxCart?.update?.({}, { info: { cartDrawerV2Upsell: true } });
        }

        if (offer.kind === 'bundle') {
          await this.removeBundleUpgradeItems(offer);
        }

        const cartDrawer = document.querySelector('cart-drawer-v2') || document.querySelector('cart-drawer');
        cartDrawer?.open?.();
      } catch (error) {
        console.error('[CartDrawerV2Upsell] Failed to add upsell product', error);
      } finally {
        this.pendingOfferHandle = null;
        this.queueRefresh();
      }
    }

    async removeBundleUpgradeItems(offer) {
      const cart = await this.getCart();
      const updates = {};
      let mattressRemoved = false;
      let protectorRemoved = false;
      const addedVariantId = Number(offer?.variant?.id || 0);
      const addedHandle = offer?.product?.handle || null;

      (cart?.items || []).forEach((item) => {
        // Never decrement the bundle that was just added: its own title can
        // contain 'protector', and it only carries _bundleItem properties when
        // its component list resolved.
        if (addedVariantId && Number(item.variant_id) === addedVariantId) {
          return;
        }

        if (addedHandle && item.handle === addedHandle) {
          return;
        }

        if (this.isBundleLineItem(item)) {
          return;
        }

        if (!mattressRemoved && this.matchesMattress(item, this.currentState?.mattressItem)) {
          updates[item.key] = Math.max(Number(item.quantity || 0) - 1, 0);
          mattressRemoved = true;
          return;
        }

        if (!protectorRemoved && this.matchesProtector(item, this.currentState?.size, this.currentState?.isKids)) {
          updates[item.key] = Math.max(Number(item.quantity || 0) - 1, 0);
          protectorRemoved = true;
        }
      });

      if (!Object.keys(updates).length || !window.liquidAjaxCart?.update) {
        return;
      }

      await window.liquidAjaxCart.update(
        { updates },
        {
          info: {
            cartDrawerV2Upsell: true,
            bundleUpgrade: true,
          },
        }
      );
    }

    isBundleLineItem(item) {
      return Object.keys(item?.properties || {}).some((key) => key.toLowerCase().includes('_bundleitem'));
    }

    matchesMattress(item, targetMattress) {
      if (!targetMattress) {
        return false;
      }

      return item.key === targetMattress.key || (item.handle === targetMattress.handle && item.title === targetMattress.title);
    }

    matchesProtector(item, size, isKids) {
      if (isKids) {
        return item.handle === this.config.kidsProtectorHandle;
      }

      const isProtector = item.handle === this.config.protectorHandle || item.title?.toLowerCase().includes('protector');

      if (!isProtector) {
        return false;
      }

      const protectorSize = this.extractSize(item.variant_title || item.title);

      return !protectorSize || protectorSize === size;
    }

    handlePointerDown(event) {
      if (!this.carousel || event.target.closest('button, modal-opener, a, input, select, textarea')) {
        return;
      }

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.carousel.scrollLeft,
      };

      this.carousel.classList.add('is-dragging');
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.carousel) {
        return;
      }

      const deltaX = event.clientX - this.dragState.startX;
      this.carousel.scrollLeft = this.dragState.startScrollLeft - deltaX;
    }

    handlePointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.carousel) {
        return;
      }

      this.dragState = null;
      this.carousel.classList.remove('is-dragging');
    }
  }

  customElements.define('cart-drawer-v2-upsell', CartDrawerV2Upsell);
}
